import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter — counts from 0 to `value` over `duration` ms once the
 * element scrolls into view. Uses requestAnimationFrame with an easeOut curve
 * so the number lands gently instead of slamming to the target.
 */
const AnimatedCounter = ({ value = 0, duration = 1600, suffix = '', prefix = '', decimals = 0, ...rest }) => {
  const [display, setDisplay] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || hasStarted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    const target = Number(value) || 0;
    if (target === 0 || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(target);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hasStarted, value, duration]);

  const formatted = decimals > 0
    ? display.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Math.round(display).toLocaleString();

  return (
    <span ref={ref} {...rest}>{prefix}{formatted}{suffix}</span>
  );
};

export default AnimatedCounter;
